import React, { useMemo, useState } from 'react';
import { DiceCatAvatar, ADMIN_AVATAR_COLOR } from './avatarTypes';
import './AvatarPreview.css';

interface AvatarPreviewProps {
  avatar?: DiceCatAvatar;
  avatarData?: DiceCatAvatar; // Alias for compatibility
  size?: number;
  userId?: string;
  isAdmin?: boolean;
  isEarlyAdopter?: boolean; // New prop for early adopter sparkles
  priority?: 'high' | 'low'; // Loading priority hint
}

// Available avatar colors for random generation
const RANDOM_COLORS = [
  '#E53935', // Red
  '#1E88E5', // Blue
  '#97fe6c', // Lime (updated from old green)
  '#FDD835', // Yellow
  '#8E24AA', // Purple
  '#FB8C00', // Orange
  '#D81B60', // Pink
  '#00897B', // Teal
];

// Map old shortened avatar item names to new camelCase names
const LEGACY_ITEM_NAMES: Record<string, string> = {
  'wizard': 'wizardHat',
  'reading': 'readingGlasses',
  'rainbow': 'rainbowFlag',
  'serious': 'neutralFace',
  'smile': 'kittySmile',
  'christmassweater': 'holidaySweater',
  'tophat': 'topHat',
  'sword': 'shield',
};

const AvatarPreviewComponent: React.FC<AvatarPreviewProps> = ({
  avatar,
  avatarData,
  size = 100,
  userId,
  isAdmin = false,
  isEarlyAdopter = false,
  priority = 'low'
}) => {
  // Use avatar or avatarData (for backward compatibility)
  const rawAvatar = avatar || avatarData;
  const [baseLoaded, setBaseLoaded] = useState(false);

  // Normalize old field names to new field names for backward compatibility
  const avatarConfig = useMemo(() => {
    if (!rawAvatar) return undefined;

    const normalized: any = { ...rawAvatar };

    // Map old field names to new ones
    if ('facial' in normalized && !normalized.face) {
      normalized.face = normalized.facial;
    }
    if ('item' in normalized && !normalized.rightHand) {
      normalized.rightHand = normalized.item;
    }
    if ('flag' in normalized && !normalized.leftHand) {
      normalized.leftHand = normalized.flag;
    }

    // Map legacy shortened item names to new camelCase names
    const fields = ['hat', 'face', 'glasses', 'rightHand', 'leftHand', 'body'];
    fields.forEach(field => {
      if (normalized[field] && LEGACY_ITEM_NAMES[normalized[field]]) {
        normalized[field] = LEGACY_ITEM_NAMES[normalized[field]];
      }
    });

    return normalized as DiceCatAvatar;
  }, [rawAvatar]);

  // Generate a consistent random color based on userId
  const randomColor = useMemo(() => {
    if (userId) {
      let hash = 0;
      for (let i = 0; i < userId.length; i++) {
        hash = userId.charCodeAt(i) + ((hash << 5) - hash);
      }
      const index = Math.abs(hash) % RANDOM_COLORS.length;
      return RANDOM_COLORS[index];
    }
    return RANDOM_COLORS[Math.floor(Math.random() * RANDOM_COLORS.length)];
  }, [userId]);

  // Use admin color if user is admin, otherwise use their chosen/random color
  const avatarColor = isAdmin ? ADMIN_AVATAR_COLOR : (avatarConfig?.color || randomColor);

  // Get the appropriate base SVG file for the color
  const baseImagePath = getBaseImagePath(avatarColor, isAdmin);

  // Handler to hide broken images
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.style.display = 'none';
    e.currentTarget.style.visibility = 'hidden';
    e.currentTarget.classList.add('broken-image');
  };

  // Determine loading strategy based on priority
  const loadingAttr = priority === 'high' ? 'eager' : 'lazy';
  const fetchPriority = priority === 'high' ? 'high' : 'low';

  return (
    <div
      className={`avatar-preview-wrapper ${isEarlyAdopter ? 'early-adopter' : ''}`}
      style={{ width: size, height: size, position: 'relative' }}
      role="img"
      aria-label={isAdmin ? "Admin avatar" : "User avatar"}
    >
      {/* Loading skeleton */}
      {!baseLoaded && (
        <div style={{
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
          background: 'var(--ion-color-light)',
          borderRadius: '50%',
          animation: 'pulse 1.5s ease-in-out infinite'
        }} />
      )}

      {/* Use regular img tags instead of SVG image elements for mobile compatibility */}
      <img
        src={baseImagePath}
        alt=""
        loading={loadingAttr as 'eager' | 'lazy'}
        fetchPriority={fetchPriority as 'high' | 'low'}
        onLoad={() => setBaseLoaded(true)}
        onError={handleImageError}
        style={{
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
          imageRendering: 'auto',
          opacity: baseLoaded ? 1 : 0,
          transition: 'opacity 0.2s ease-in'
        }}
      />

      {/* Layer accessories on top - only load after base is loaded */}
      {baseLoaded && avatarConfig?.body && avatarConfig.body !== 'null' && (
        <img
          src={`/assets/${avatarConfig.body}.svg`}
          alt=""
          loading="lazy"
          onError={handleImageError}
          style={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            imageRendering: 'auto'
          }}
        />
      )}

      {baseLoaded && avatarConfig?.hat && avatarConfig.hat !== 'null' && (
        <img
          src={`/assets/${avatarConfig.hat}.svg`}
          alt=""
          loading="lazy"
          onError={handleImageError}
          style={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            imageRendering: 'auto'
          }}
        />
      )}

      {baseLoaded && avatarConfig?.face && avatarConfig.face !== 'null' && (
        <img
          src={`/assets/${avatarConfig.face}.svg`}
          alt=""
          loading="lazy"
          onError={handleImageError}
          style={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            imageRendering: 'auto'
          }}
        />
      )}

      {baseLoaded && avatarConfig?.glasses && avatarConfig.glasses !== 'null' && (
        <img
          src={`/assets/${avatarConfig.glasses}.svg`}
          alt=""
          loading="lazy"
          onError={handleImageError}
          style={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            imageRendering: 'auto'
          }}
        />
      )}

      {baseLoaded && avatarConfig?.rightHand && avatarConfig.rightHand !== 'null' && (
        <img
          src={`/assets/${avatarConfig.rightHand}.svg`}
          alt=""
          loading="lazy"
          onError={handleImageError}
          style={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            imageRendering: 'auto'
          }}
        />
      )}

      {baseLoaded && avatarConfig?.leftHand && avatarConfig.leftHand !== 'null' && (
        <img
          src={`/assets/${avatarConfig.leftHand}.svg`}
          alt=""
          loading="lazy"
          onError={handleImageError}
          style={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            imageRendering: 'auto'
          }}
        />
      )}

      {/* Early Adopter Sparkle Effect */}
      {isEarlyAdopter && (
        <>
          <span className="sparkle sparkle-1">✨</span>
          <span className="sparkle sparkle-2">✨</span>
          <span className="sparkle sparkle-3">✨</span>
        </>
      )}
    </div>
  );
};

// Memoize component to prevent unnecessary re-renders
export const AvatarPreview = React.memo(AvatarPreviewComponent, (prevProps, nextProps) => {
  // Only re-render if these specific props change
  return (
    prevProps.userId === nextProps.userId &&
    prevProps.size === nextProps.size &&
    prevProps.isAdmin === nextProps.isAdmin &&
    prevProps.isEarlyAdopter === nextProps.isEarlyAdopter &&
    prevProps.priority === nextProps.priority &&
    JSON.stringify(prevProps.avatar || prevProps.avatarData) === JSON.stringify(nextProps.avatar || nextProps.avatarData)
  );
});

// Map avatar color to pre-colored base SVG file
function getBaseImagePath(color: string, isAdmin: boolean): string {
  // Admin users get the special admin base
  if (isAdmin) {
    return '/assets/adminBase.svg';
  }

  // Map hex colors to base SVG filenames
  const colorMap: Record<string, string> = {
    '#E53935': '/assets/redBase.svg',      // Red
    '#1E88E5': '/assets/blueBase.svg',     // Blue
    '#97fe6c': '/assets/greenBase.svg',    // Lime/Green
    '#FDD835': '/assets/yellowBase.svg',   // Yellow
    '#8E24AA': '/assets/purpleBase.svg',   // Purple
    '#FB8C00': '/assets/orangeBase.svg',   // Orange
    '#D81B60': '/assets/pinkBase.svg',     // Pink
    '#00897B': '/assets/tealBase.svg',     // Teal
    '#6D4C41': '/assets/brownBase.svg',    // Brown
    '#424242': '/assets/blackBase.svg',    // Black
    '#FAFAFA': '/assets/whiteBase.svg',    // White
    '#757575': '/assets/greyBase.svg',     // Gray
    '#16a75a': '/assets/adminBase.svg',    // Admin color (fallback)
  };

  return colorMap[color] || '/assets/whiteBase.svg'; // Fallback to white base
}
